import React, { useState } from 'react';
import './RecordDetailModal.css';
import { EvaluationRecord, ACECategoryScore } from '../../services/api';
import { ScoreRing } from '../../components/ScoreRing/ScoreRing';
import { AudioPlayer } from '../../components/AudioPlayer/AudioPlayer';
import { API_BASE_URL } from '../../services/api';

const getAudioUrl = (audioPath: string | null): string | null => {
  if (!audioPath) return null;
  // 如果已经是完整URL，直接返回
  if (audioPath.startsWith('http://') || audioPath.startsWith('https://')) {
    return audioPath;
  }
  // 否则拼接API基础URL
  return API_BASE_URL + audioPath;
};

interface RecordDetailModalProps {
  record: EvaluationRecord;
  onClose: () => void;
}

const ACE_CATEGORY_NAMES: Record<string, string> = {
  'Accurate': '准确性',
  'Customized': '个性化',
  'Efficient': '高效性'
};

const LANGUAGE_NAMES: Record<string, string> = {
  'zh': '中文',
  'en': '英语',
  'ko': '韩语',
  'ja': '日语',
  'es': '西班牙语',
  'fr': '法语',
  'de': '德语'
};

export const RecordDetailModal: React.FC<RecordDetailModalProps> = ({ record, onClose }) => {
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('zh-CN', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit', second: '2-digit',
    });
  };

  const formatFileSize = (bytes: number | null) => {
    if (!bytes) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(2)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(2)} MB`;
  };

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return 'N/A';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getSentimentLabel = (sentiment: string) => {
    const labels: Record<string, string> = { positive: '积极', neutral: '中性', negative: '消极' };
    return labels[sentiment] || sentiment;
  };

  const toggleCategory = (category: string) => {
    setExpandedCategory(expandedCategory === category ? null : category);
  };

  const hasAceScores = record.ace_scores && record.ace_scores.length > 0;
  const detectedLang = record.detected_language || 'zh';

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>ACE质检记录详情</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        <div className="modal-body">
          {/* ZTP警告 */}
          {record.ztp_violation && (
            <div className="ztp-warning-modal">
              <span className="ztp-icon">⚠️</span>
              <div className="ztp-content">
                <strong>零容忍政策(ZTP)违规</strong>
                <p>{record.ztp_details || '检测到严重违规行为'}</p>
              </div>
            </div>
          )}

          {/* 基本信息 */}
          <section className="detail-section">
            <h3>基本信息</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">记录 ID:</span>
                <span className="info-value">{record.id}</span>
              </div>
              <div className="info-item">
                <span className="info-label">文件名:</span>
                <span className="info-value">{record.filename}</span>
              </div>
              <div className="info-item">
                <span className="info-label">文件大小:</span>
                <span className="info-value">{formatFileSize(record.file_size)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">音频时长:</span>
                <span className="info-value">{formatDuration(record.audio_duration)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">检测语言:</span>
                <span className="info-value">{LANGUAGE_NAMES[detectedLang] || detectedLang}</span>
              </div>
              <div className="info-item">
                <span className="info-label">创建时间:</span>
                <span className="info-value">{formatDate(record.created_at)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">情绪倾向:</span>
                <span className={`sentiment-badge sentiment-${record.sentiment}`}>
                  {getSentimentLabel(record.sentiment)}
                </span>
              </div>
            </div>
          </section>

          {/* 音频播放器 */}
          {getAudioUrl(record.audio_file_path) && (
            <section className="detail-section">
              <h3>语音回放</h3>
              <AudioPlayer
                audioUrl={getAudioUrl(record.audio_file_path)!}
                filename={record.filename}
              />
            </section>
          )}

          {/* ACE评分概览 */}
          <section className="detail-section">
            <h3>ACE评分概览</h3>
            <div className="scores-grid ace-scores-grid">
              <div className="score-item score-item-main">
                <ScoreRing score={record.overall_score} size={90} />
                <div className="score-label">综合评分</div>
              </div>
              <div className="score-item">
                <ScoreRing score={record.accurate_score ?? 0} size={70} />
                <div className="score-label">Accurate<br/>准确性</div>
              </div>
              <div className="score-item">
                <ScoreRing score={record.customized_score ?? 0} size={70} />
                <div className="score-label">Customized<br/>个性化</div>
              </div>
              <div className="score-item">
                <ScoreRing score={record.efficient_score ?? 0} size={70} />
                <div className="score-label">Efficient<br/>高效性</div>
              </div>
            </div>
          </section>

          {/* ACE详细评分 */}
          {hasAceScores && (
            <section className="detail-section">
              <h3>ACE详细评分指标（共19项）</h3>
              <div className="ace-details-container">
                {record.ace_scores!.map((category: ACECategoryScore) => (
                  <div key={category.category} className="ace-category-block-modal">
                    <div 
                      className={`ace-category-header-modal ${expandedCategory === category.category ? 'expanded' : ''}`}
                      onClick={() => toggleCategory(category.category)}
                    >
                      <span className="ace-cat-name">
                        {category.category} · {ACE_CATEGORY_NAMES[category.category]}
                      </span>
                      <span className="ace-cat-score">{category.score}分</span>
                      <span className="ace-cat-toggle">{expandedCategory === category.category ? '▼' : '▶'}</span>
                    </div>
                    {expandedCategory === category.category && (
                      <div className="ace-criteria-list-modal">
                        {category.criteria.map((criterion) => (
                          <div key={criterion.id} className={`ace-criterion-modal ${criterion.passed ? 'passed' : 'failed'}`}>
                            <span className="criterion-status">{criterion.passed ? '✓' : '✗'}</span>
                            <span className="criterion-name">{criterion.name}</span>
                            <span className="criterion-score">{criterion.score}分</span>
                            <span className="criterion-comment">{criterion.comment}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 总体评价 */}
          <section className="detail-section">
            <h3>总体评价</h3>
            <div className="summary-box">{record.summary}</div>
          </section>

          {/* 对话时间线（含翻译） */}
          {record.timeline.length > 0 && (
            <section className="detail-section">
              <h3>对话时间线</h3>
              <div className="timeline-container">
                {record.timeline.map((utterance, idx) => (
                  <div key={idx} className={`timeline-item timeline-${utterance.speaker}`}>
                    <div className="timeline-header-modal">
                      <span className="timeline-speaker-modal">
                        {utterance.speaker === 'agent' ? '🎧 坐席' : '👤 客户'}
                      </span>
                      {utterance.language && utterance.language !== 'zh' && (
                        <span className="timeline-lang-modal">
                          {LANGUAGE_NAMES[utterance.language] || utterance.language}
                        </span>
                      )}
                    </div>
                    <div className="timeline-text">{utterance.text}</div>
                    {utterance.textCn && utterance.language !== 'zh' && (
                      <div className="timeline-translation-modal">
                        <span className="translation-label-modal">中文：</span>
                        {utterance.textCn}
                      </div>
                    )}
                    {(utterance.politePhrases?.length > 0 || utterance.rudePhrases?.length > 0) && (
                      <div className="timeline-tags">
                        {utterance.politePhrases?.map((phrase, i) => (
                          <span key={`p-${i}`} className="tag tag-success-sm">{phrase}</span>
                        ))}
                        {utterance.rudePhrases?.map((phrase, i) => (
                          <span key={`r-${i}`} className="tag tag-danger-sm">{phrase}</span>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </section>
          )}

          {/* 对话流程 */}
          <section className="detail-section">
            <h3>对话流程</h3>
            <div className="flow-box">{record.dialogue_flow}</div>
          </section>

          {/* 检测结果 */}
          <section className="detail-section">
            <h3>检测结果</h3>
            <div className="detection-grid">
              {record.polite_phrases.length > 0 && (
                <div className="detection-item">
                  <div className="detection-label">✓ 礼貌用语</div>
                  <div className="tags-container">
                    {record.polite_phrases.map((phrase, idx) => (
                      <span key={idx} className="tag tag-success">{phrase}</span>
                    ))}
                  </div>
                </div>
              )}
              {record.missing_polite_phrases.length > 0 && (
                <div className="detection-item">
                  <div className="detection-label">⚠ 建议补充</div>
                  <div className="tags-container">
                    {record.missing_polite_phrases.map((phrase, idx) => (
                      <span key={idx} className="tag tag-warning">{phrase}</span>
                    ))}
                  </div>
                </div>
              )}
              {record.rude_phrases.length > 0 && (
                <div className="detection-item">
                  <div className="detection-label">✗ 不当用语</div>
                  <div className="tags-container">
                    {record.rude_phrases.map((phrase, idx) => (
                      <span key={idx} className="tag tag-danger">{phrase}</span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </section>

          {/* 改进建议 */}
          {record.suggestions.length > 0 && (
            <section className="detail-section">
              <h3>改进建议（基于ACE评价体系）</h3>
              <ul className="suggestions-list">
                {record.suggestions.map((suggestion, idx) => (
                  <li key={idx}>{suggestion}</li>
                ))}
              </ul>
            </section>
          )}

          {/* 完整转录 */}
          {record.transcription && (
            <section className="detail-section">
              <h3>完整转录（{LANGUAGE_NAMES[detectedLang] || detectedLang}）</h3>
              <div className="transcription-box">{record.transcription}</div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
};
